// The send tray's plan: what each picked item is, the order it goes in, and which photos and
// videos travel together as an album. The tray screen keeps the state. This file only decides.

import { type AlbumMark, albumMarks, type FileMeta } from "@rhema/client";
import { extensionOf } from "./prepare";

export type TrayKind = "photo" | "video" | "file";

/** One picked item, waiting in the tray. */
export type TrayItem = {
  id: string;
  file: File;
  kind: TrayKind;
  caption: string;
};

/** An item made ready to encrypt: a photo resized and cleaned, a video with its poster. */
export type Prepared = { file: Blob; kind: TrayKind; meta: FileMeta };

/** One message to send, in sending order. */
export type PlannedSend = {
  item: TrayItem;
  prepared: Prepared;
  album?: AlbumMark;
};

const PHOTO_EXT = new Set(["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "avif"]);
const VIDEO_EXT = new Set(["mp4", "mov", "m4v", "webm"]);

/**
 * What a picked file is. iPhones can hand over a HEIC photo with no type, so the name counts too.
 * SVG is drawn from code that could reach out to the web, so it always goes as a file.
 */
export function trayKind(file: File, asFile = false): TrayKind {
  if (asFile) return "file";
  const ext = extensionOf(file.name, "").toLowerCase();
  if (file.type === "image/svg+xml") return "file";
  if (file.type.startsWith("image/") || (!file.type && PHOTO_EXT.has(ext))) return "photo";
  if (file.type.startsWith("video/") || (!file.type && VIDEO_EXT.has(ext))) return "video";
  return "file";
}

let counter = 0;

export function trayItems(files: File[], asFile = false): TrayItem[] {
  return files.map((file) => ({
    id: `tray-${Date.now().toString(36)}-${(counter++).toString(36)}`,
    file,
    kind: trayKind(file, asFile),
    caption: "",
  }));
}

/** The list with the item at `from` moved to `to`. */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length) return [...list];
  const out = [...list];
  const [moved] = out.splice(from, 1);
  out.splice(Math.max(0, Math.min(out.length, to)), 0, moved as T);
  return out;
}

/**
 * The order messages go out in. Photos and videos go first, in the tray's order, and travel as
 * albums of up to ten. Files follow as messages of their own, so nothing lands in the middle of an
 * album. A photo that could not be read goes as a file and leaves the album.
 */
export function planSend(items: readonly TrayItem[], prepared: readonly Prepared[]): PlannedSend[] {
  const pairs = items.map((item, i) => ({ item, prepared: prepared[i] as Prepared }));
  const visual = pairs.filter((p) => p.prepared.kind === "photo" || p.prepared.kind === "video");
  const files = pairs.filter((p) => p.prepared.kind === "file");
  const marks = albumMarks(visual.length);
  return [
    ...visual.map((p, i) => {
      const album = marks[i];
      return album ? { ...p, album } : p;
    }),
    ...files,
  ];
}

/** "4 items", "1 photo", "3 videos". */
export function countLabel(items: readonly { kind: TrayKind }[]): string {
  const n = items.length;
  const kinds = new Set(items.map((i) => i.kind));
  if (kinds.size !== 1) return n === 1 ? "1 item" : `${n} items`;
  const kind = [...kinds][0] as TrayKind;
  const noun = { photo: "photo", video: "video", file: "file" }[kind];
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

/** Where each label in the tray comes from: "Photo 2", "Video 3", or the file's name. */
export function itemLabel(item: TrayItem, position: number): string {
  if (item.kind === "photo") return `Photo ${position + 1}`;
  if (item.kind === "video") return `Video ${position + 1}`;
  return item.file.name || `File ${position + 1}`;
}
